import { lazy, ComponentType } from 'react';

/**
 * lazyWithRetry — กัน "หน้า 500 เกิดข้อผิดพลาดในการแสดงผล" หลัง deploy frontend
 *
 * ทุกครั้งที่ deploy ชื่อไฟล์ chunk (assets/XxxPage-<hash>.js) เปลี่ยน → tab ที่เปิดค้างอยู่ยังถือ
 * index.html เก่า พอ navigate ไป route ที่ยังไม่เคยโหลด browser ขอ chunk ชื่อเก่าซึ่งไม่มีแล้ว
 * → dynamic import ล้ม → ErrorBoundary โชว์หน้า 500 ผู้ใช้ต้อง refresh เอง
 * (user report Mr.Hop 2026-08-26 หลัง deploy frontend 4 รอบในวันเดียว)
 *
 * วิธีแก้: ถ้า import ล้มด้วย error แบบ chunk หาย → reload หน้า 1 ครั้ง (ได้ index.html + chunk ชุดใหม่)
 * มี guard ใน sessionStorage กัน loop ถ้า reload แล้วยังล้ม → ปล่อย error ให้ ErrorBoundary ตามเดิม
 */

const RELOAD_FLAG_KEY = 'krp:stale-chunk-reloaded';

/**
 * SENTRY KRP-FRONTEND-3/8/9/D/E (prod 3 พ.ย. 2026 · หลัง deploy frontend 4 รอบ):
 * `vite:preloadError` (main.tsx) สั่ง reload + `preventDefault()` → Vite **resolve การ import เป็น undefined**
 * แล้ว `loadChunkWithRetry` เห็นโมดูลว่าง เรียก `reloadOnceForStaleChunk()` ซ้ำ ได้ false (flag เพิ่งถูกตั้ง)
 * → คืน undefined ให้ React → "reading 'default'" → ErrorBoundary + Sentry **ก่อน** reload จะเกิดจริง
 * ⇒ จำในหน่วยความจำของหน้านี้ว่า "สั่ง reload ไปแล้ว รออยู่" — ระหว่างนั้นทุกทางต้องรอ ไม่ใช่โยน/คืนของว่าง
 */
let reloadPending = false;

/** หน้านี้สั่ง reload เพราะ chunk หายไปแล้วและกำลังรอหน้าใหม่ */
export const isStaleChunkReloadPending = (): boolean => reloadPending;

/** สำหรับเทสต์เท่านั้น */
export const __resetStaleChunkReloadPending = (): void => {
  reloadPending = false;
};

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk [\w-]+ failed|Loading CSS chunk|ChunkLoadError|Unable to preload CSS/i;

export const isChunkLoadError = (error: unknown): boolean => {
  const message =
    typeof error === 'string'
      ? error
      : (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && CHUNK_ERROR_PATTERN.test(message);
};

/** reload 1 ครั้งต่อ session-ช่วง (flag ถูกล้างหลังโหลดสำเร็จ ดู clearStaleChunkReloadFlag) — คืน true ถ้าสั่ง reload แล้ว */
export const reloadOnceForStaleChunk = (): boolean => {
  if (reloadPending) return true; // สั่งไปแล้วในหน้านี้ — ถือว่ากำลัง reload (ห้ามตอบ false แล้วปล่อยของพังออกไป)
  try {
    if (sessionStorage.getItem(RELOAD_FLAG_KEY)) {
      return false;
    }
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch {
    // sessionStorage ใช้ไม่ได้ (private mode ฯลฯ) → ยัง reload ได้ แต่ไม่มี guard; ยอมรับ
  }
  reloadPending = true;
  window.location.reload();
  return true;
};

/** เรียกหลังแอพโหลดสำเร็จสักพัก เพื่อให้ deploy รอบถัดไป trigger reload ได้อีก */
export const clearStaleChunkReloadFlag = (): void => {
  try {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * module ที่โหลดมา "ใช้ได้จริง" หรือไม่ — ต้องมี `default` ที่เป็น component
 *
 * SENTRY KRP-FRONTEND-3/4/8/9 (prod, 14 event / 7 ผู้ใช้ · ล่าสุด 4 ก.ย.):
 * chunk เก่าที่หายหลัง deploy **ไม่ได้ reject เสมอไป** — บางครั้ง import สำเร็จแต่ได้ค่าว่าง
 * (เช่น server ตอบ index.html แทนไฟล์ .js ที่ไม่มีแล้ว) ⇒ promise resolve ปกติ
 * `.catch()` ด้านล่างจึงไม่ทำงานเลย แล้วไปพังที่ React `lazyInitializer` ตอนอ่าน
 * `payload._result.default` → "Cannot read properties of undefined (reading 'default')"
 * ผู้ใช้เห็นหน้าแจ้งข้อผิดพลาดและต้อง refresh เอง ทั้งที่ตัวกัน stale chunk มีอยู่แล้ว
 */
const isUsableModule = <T,>(mod: unknown): mod is { default: T } =>
  !!mod && typeof mod === 'object' && (mod as { default?: unknown }).default != null;

/**
 * ตรรกะโหลด chunk ทั้งหมด แยกออกมาให้เทสได้โดยไม่ต้องพึ่ง internal ของ React `lazy()`
 *
 * คืน promise ที่ "ค้างตลอดกาล" เมื่อสั่ง reload แล้ว — ให้ Suspense โชว์ "กำลังโหลด…"
 * จนหน้าใหม่มาแทน แทนที่จะโยน error ให้ ErrorBoundary
 */
export function loadChunkWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): Promise<{ default: T }> {
  return factory()
    .then((mod) => {
      // resolve แล้วแต่ได้ของว่าง = chunk หายแบบไม่ throw → ปฏิบัติเหมือน chunk load error
      if (!isUsableModule<T>(mod) && reloadOnceForStaleChunk()) {
        return new Promise<{ default: T }>(() => {});
      }
      return mod;
    })
    .catch((error: unknown) => {
      if (isChunkLoadError(error) && reloadOnceForStaleChunk()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    });
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() => loadChunkWithRetry(factory));
}

export default lazyWithRetry;
